import { useCallback, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { t } from 'i18next';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import { Dialog } from 'primereact/dialog';
import { FormEvent } from 'primereact/ts-helpers';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Equipment, Owner, SEARCH_HERDSMEN, SEARCH_OWNERS } from '@/types.gql';

import { useCalfEntryContext } from './useCalfEntryContext';

type CalfEntryRequirementsProps = {
  className?: string;
};

export const CalfEntryRequirements = ({
  className,
}: CalfEntryRequirementsProps) => {
  const {
    receivedDate,
    setReceivedDate,
    setOwner,
    setHerdsman,
    owner,
    herdsman,
    calves,
  } = useCalfEntryContext();

  const [dialogVisible, setDialogVisible] = useState(false);

  const [ownerQuery] = useLazyQuery(SEARCH_OWNERS);
  const [ownerSearch, setOwnerSearch] = useState<string>();
  const [owners, setOwners] = useState<Owner[]>();

  const [herdsmanQuery] = useLazyQuery(SEARCH_HERDSMEN);
  const [herdsmanSearch, setHerdsmanSearch] = useState<string>();
  const [herdsmen, setHerdsmen] = useState<Equipment[]>();

  const isFormDisabled =
    receivedDate !== undefined &&
    owner !== null &&
    herdsman !== null &&
    calves.length > 0;

  const handleDateChange = useCallback(
    (e: FormEvent) => {
      setReceivedDate(e.value);
    },
    [setReceivedDate],
  );

  const handleOwnerChange = useCallback(
    (e: FormEvent) => {
      if (!e.value) {
        setOwner(null);
      }
      setOwnerSearch(e.value);
    },
    [setOwner],
  );

  const handleOwnerComplete = useCallback(
    (e: AutoCompleteCompleteEvent) => {
      const result = ownerQuery({
        variables: {
          search: e.query,
          selectionLimit: SELECTION_LIMIT,
        },
      });

      result.then((res) => {
        setOwners(
          res.data?.accounts?.items?.map((item) => {
            return {
              id: item?.id,
              name: item?.name,
              code: item?.code,
            };
          }) as Owner[],
        );
      });
    },
    [ownerQuery],
  );

  const handleOwnerSelect = useCallback(
    (e: AutoCompleteSelectEvent) => {
      setOwner(e.value);
    },
    [setOwner],
  );

  const handleHerdsmanChange = useCallback(
    (e: FormEvent) => {
      if (!e.value) {
        setHerdsman(null);
      }
      setHerdsmanSearch(e.value);
    },
    [setHerdsman],
  );

  const handleHerdsmanComplete = useCallback(
    (e: AutoCompleteCompleteEvent) => {
      const result = herdsmanQuery({
        variables: {
          search: e.query,
          selectionLimit: SELECTION_LIMIT,
        },
      });

      result.then((res) => {
        setHerdsmen(res.data?.equipments?.items as Equipment[]);
      });
    },
    [herdsmanQuery],
  );

  const handleHerdsmanSelect = useCallback(
    (e: AutoCompleteSelectEvent) => {
      setHerdsman(e.value);
    },
    [setHerdsman],
  );

  const handlePanelClick = () => {
    if (!isFormDisabled) return;
    setDialogVisible(true);
  };

  return (
    <>
      <div
        className={`calf-entry-requirements content-panel glass ${className}`.trim()}
        onClick={handlePanelClick}
      >
        <div className="calf-entry-requirements__received-date label-input-group">
          <label>{t('calfEntry.receivedDate')}</label>
          <Calendar
            value={receivedDate ? new Date(receivedDate) : undefined}
            dateFormat="mm-dd-yy"
            onChange={handleDateChange}
            showIcon
            invalid={!receivedDate}
            disabled={isFormDisabled}
          />
        </div>
        <div className="calf-entry-requirements__owner label-input-group">
          <label>{t('account.owner')}</label>
          <AutoComplete
            value={ownerSearch}
            field={'name'}
            suggestions={owners}
            minLength={MIN_SEARCH_LENGTH}
            selectionLimit={SELECTION_LIMIT}
            onChange={handleOwnerChange}
            onSelect={handleOwnerSelect}
            completeMethod={handleOwnerComplete}
            disabled={isFormDisabled}
          />
        </div>
        <div className="calf-entry-requirements__herdsman label-input-group">
          <label>{t('calfEntry.herdsman')}</label>
          <AutoComplete
            value={herdsmanSearch}
            field={'name'}
            suggestions={herdsmen}
            minLength={MIN_SEARCH_LENGTH}
            selectionLimit={SELECTION_LIMIT}
            onChange={handleHerdsmanChange}
            onSelect={handleHerdsmanSelect}
            completeMethod={handleHerdsmanComplete}
            disabled={isFormDisabled}
          />
        </div>
      </div>
      <Dialog
        header="Cannot Change Intake Requirements"
        visible={dialogVisible}
        style={{ width: '32rem' }}
        onHide={() => {
          if (!dialogVisible) return;
          setDialogVisible(false);
        }}
      >
        <p className="m-0">
          You currently have unsaved calf entries. You will need to save or
          delete them before you can change the intake requirements.
        </p>
      </Dialog>
    </>
  );
};
